(function () {
  'use strict';

  var MAX_PRODUCTS = 4;
  var productCache = {};
  var refreshSequence = 0;
  var refreshTimer = null;

  var IGNORED_VALUES = [
    'RES',
    'S-RES',
    'SX-RES',
    'L-RES',
    'LX-RES'
  ];

  function normalise(value) {
    return String(value || '')
      .trim()
      .toLowerCase();
  }

  function isIgnoredValue(value) {
    return IGNORED_VALUES.indexOf(
      String(value || '')
        .trim()
        .toUpperCase()
    ) !== -1;
  }

  function splitValues(value) {
    return String(value || '')
      .split('|')
      .map(function (item) {
        return normalise(item);
      })
      .filter(function (item) {
        return Boolean(item);
      });
  }

  function arraysOverlap(arrayA, arrayB) {
    return arrayA.some(function (value) {
      return arrayB.indexOf(value) !== -1;
    });
  }

  function getOptionNames(product) {
    return (product.options || []).map(
      function (option) {
        return typeof option === 'string'
          ? option
          : option.name;
      }
    );
  }

  function findOptionIndex(product, wantedName) {
    var wanted = normalise(wantedName);

    return getOptionNames(product).findIndex(
      function (name) {
        return normalise(name) === wanted;
      }
    );
  }

  function getProductData(handle) {
    if (!handle) {
      return Promise.resolve(null);
    }

    if (productCache[handle]) {
      return Promise.resolve(
        productCache[handle]
      );
    }

    return fetch(
      '/products/' +
        encodeURIComponent(handle) +
        '.js',
      {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json'
        }
      }
    )
      .then(function (response) {
        if (!response.ok) {
          throw new Error(
            'Unable to load recommendation product: ' +
              handle
          );
        }

        return response.json();
      })
      .then(function (product) {
        productCache[handle] = product;

        return product;
      });
  }

  function findSizeVariant(product, size) {
    if (
      !product ||
      !Array.isArray(product.variants)
    ) {
      return null;
    }

    var neck = String(
      size.neck || ''
    ).trim();

    var sleeve = String(
      size.sleeve || ''
    ).trim();

    if (isIgnoredValue(neck)) {
      neck = '';
    }

    if (isIgnoredValue(sleeve)) {
      sleeve = '';
    }

    if (!neck && !sleeve) {
      return null;
    }

    var neckIndex = findOptionIndex(
      product,
      'Neck Size'
    );

    var sleeveIndex = findOptionIndex(
      product,
      'Sleeve Length'
    );

    return (
      product.variants.find(
        function (variant) {
          if (!variant.available) {
            return false;
          }

          if (neck) {
            if (neckIndex === -1) {
              return false;
            }

            if (
              String(
                variant.options[neckIndex]
              ) !== neck
            ) {
              return false;
            }
          }

          if (sleeve) {
            if (sleeveIndex === -1) {
              return false;
            }

            if (
              String(
                variant.options[sleeveIndex]
              ) !== sleeve
            ) {
              return false;
            }
          }

          return true;
        }
      ) || null
    );
  }

  function getRelationshipData(
    candidate,
    context
  ) {
    var candidateFabric = normalise(
      candidate.dataset.xFabric
    );

    var candidateStyle = normalise(
      candidate.dataset.xStyle
    );

    var candidateStyleCode = normalise(
      candidate.dataset.xStyleCode
    );

    var candidateColour = normalise(
      candidate.dataset.colour
    );

    var candidateFit = normalise(
      candidate.dataset.fit
    );

    var candidateSeason = normalise(
      candidate.dataset.xSeasonCode
    );

    var candidateOccasions = splitValues(
      candidate.dataset.xOccasions
    );

    var currentFabric = normalise(
      context.dataset.xFabric
    );

    var currentStyle = normalise(
      context.dataset.xStyle
    );

    var currentStyleCode = normalise(
      context.dataset.xStyleCode
    );

    var currentColour = normalise(
      context.dataset.productColour
    );

    var currentFit = normalise(
      context.dataset.productFit
    );

    var currentSeason = normalise(
      context.dataset.xSeasonCode
    );

    var currentOccasions = splitValues(
      context.dataset.xOccasions
    );

    return {
      sameFabric:
        Boolean(candidateFabric) &&
        Boolean(currentFabric) &&
        candidateFabric === currentFabric,

      sameStyle:
        Boolean(candidateStyle) &&
        Boolean(currentStyle) &&
        candidateStyle === currentStyle,

      sameStyleCode:
        Boolean(candidateStyleCode) &&
        Boolean(currentStyleCode) &&
        candidateStyleCode ===
          currentStyleCode,

      sameColour:
        Boolean(candidateColour) &&
        Boolean(currentColour) &&
        candidateColour === currentColour,

      sameSeason:
        Boolean(candidateSeason) &&
        Boolean(currentSeason) &&
        candidateSeason === currentSeason,

      sameOccasion:
        candidateOccasions.length > 0 &&
        currentOccasions.length > 0 &&
        arraysOverlap(
          candidateOccasions,
          currentOccasions
        ),

      sameFit:
        Boolean(candidateFit) &&
        Boolean(currentFit) &&
        candidateFit === currentFit
    };
  }

  function getRecommendationTier(
    relationships,
    hasExactSize
  ) {
    /*
     * A product without the exact available Neck/Sleeve
     * combination is backfill only.
     */
    if (!hasExactSize) {
      return 8;
    }

    /*
     * Exact-size hierarchy:
     *
     * 1. Matching x-fabric
     * 2. Matching x-style
     * 3. Matching x-styleCode
     * 4. Matching filter.colour
     * 5. Matching x-seasonCode
     * 6. Overlapping x-occasion
     * 7. Other exact-size same-fit products
     * 8. Same-fit products without exact available size
     */
    if (relationships.sameFabric) {
      return 1;
    }

    if (relationships.sameStyle) {
      return 2;
    }

    if (relationships.sameStyleCode) {
      return 3;
    }

    if (relationships.sameColour) {
      return 4;
    }

    if (relationships.sameSeason) {
      return 5;
    }

    if (relationships.sameOccasion) {
      return 6;
    }

    return 7;
  }

  function getRecommendationScore(
    candidate,
    relationships,
    context
  ) {
    var score = 0;

    /*
     * These values rank candidates within a tier.
     * Recommendation tier remains the primary rule.
     */
    if (relationships.sameFabric) {
      score += 100;
    }

    if (relationships.sameStyle) {
      score += 70;
    }

    if (relationships.sameStyleCode) {
      score += 50;
    }

    if (relationships.sameColour) {
      score += 30;
    }

    if (relationships.sameSeason) {
      score += 20;
    }

    if (relationships.sameOccasion) {
      score += 15;
    }

    if (relationships.sameFit) {
      score += 10;
    }

    /*
     * Sale-aware preference:
     *
     * Sale PDP:
     * sale candidates receive +25.
     *
     * Full-price PDP:
     * full-price candidates receive +25.
     *
     * The 25-point difference exceeds the 20-point
     * variety threshold, so the preferred sale state
     * remains ahead within the same tier.
     */
    var currentProductOnSale =
      context.dataset.productOnSale === 'true';

    var candidateOnSale =
      candidate.dataset.onSale === 'true';

    if (
      currentProductOnSale === candidateOnSale
    ) {
      score += 25;
    }

    return score;
  }

  function getFamily(candidate) {
    return (
      normalise(
        candidate.dataset.xFabric
      ) ||
      normalise(
        candidate.dataset.xStyle
      ) ||
      normalise(
        candidate.dataset.xStyleCode
      ) ||
      normalise(
        candidate.dataset.productHandle
      ) ||
      String(
        candidate.dataset.productId || ''
      )
    );
  }

  function sortComparableCandidates(
    candidates
  ) {
    candidates.forEach(
      function (candidate) {
        candidate.dataset.varietyOrder =
          String(Math.random());
      }
    );

    candidates.sort(
      function (candidateA, candidateB) {
        var scoreA = Number(
          candidateA.dataset
            .recommendationScore || 0
        );

        var scoreB = Number(
          candidateB.dataset
            .recommendationScore || 0
        );

        var scoreDifference =
          scoreB - scoreA;

        /*
         * Preserve meaningful merchandising advantages,
         * including the 25-point sale-context bonus.
         */
        if (
          Math.abs(scoreDifference) >= 20
        ) {
          return scoreDifference;
        }

        /*
         * Otherwise rotate broadly comparable products.
         */
        return (
          Number(
            candidateA.dataset
              .varietyOrder || 0
          ) -
          Number(
            candidateB.dataset
              .varietyOrder || 0
          )
        );
      }
    );

    return candidates;
  }

  function selectWithDiversity(candidates) {
    var selected = [];
    var familyCounts = {};
    var tiers = {};

    candidates.forEach(
      function (candidate) {
        var tier = Number(
          candidate.dataset
            .recommendationTier || 99
        );

        if (!tiers[tier]) {
          tiers[tier] = [];
        }

        tiers[tier].push(candidate);
      }
    );

    Object.keys(tiers)
      .map(function (tier) {
        return Number(tier);
      })
      .sort(function (tierA, tierB) {
        return tierA - tierB;
      })
      .forEach(function (tier) {
        var tierCandidates =
          sortComparableCandidates(
            tiers[tier].slice()
          );

        tierCandidates.forEach(
          function (candidate) {
            if (
              selected.length >= MAX_PRODUCTS
            ) {
              return;
            }

            var family =
              getFamily(candidate);

            var familyCount =
              familyCounts[family] || 0;

            /*
             * Allow up to two products from one
             * fabric/style family, then diversify.
             */
            if (familyCount >= 2) {
              return;
            }

            selected.push(candidate);

            familyCounts[family] =
              familyCount + 1;
          }
        );
      });

    /*
     * If diversity leaves fewer than four, backfill using
     * the strongest unused candidates.
     */
    if (selected.length < MAX_PRODUCTS) {
      var unusedCandidates =
        candidates.filter(
          function (candidate) {
            return (
              selected.indexOf(candidate) === -1
            );
          }
        );

      unusedCandidates.sort(
        function (
          candidateA,
          candidateB
        ) {
          var tierDifference =
            Number(
              candidateA.dataset
                .recommendationTier || 99
            ) -
            Number(
              candidateB.dataset
                .recommendationTier || 99
            );

          if (tierDifference !== 0) {
            return tierDifference;
          }

          return (
            Number(
              candidateB.dataset
                .recommendationScore || 0
            ) -
            Number(
              candidateA.dataset
                .recommendationScore || 0
            )
          );
        }
      );

      unusedCandidates.forEach(
        function (candidate) {
          if (
            selected.length >= MAX_PRODUCTS
          ) {
            return;
          }

          selected.push(candidate);
        }
      );
    }

    return selected;
  }

  function storeOriginalProductLinks(
    candidate
  ) {
    candidate
      .querySelectorAll(
        'a.product-link[href*="/products/"]'
      )
      .forEach(function (link) {
        if (
          !link.dataset
            .fitSizeOriginalHref
        ) {
          link.dataset.fitSizeOriginalHref =
            link.getAttribute('href');
        }
      });
  }

  function applyProductLinks(candidate) {
    var variantId =
      candidate.dataset.matchingVariantId;

    candidate
      .querySelectorAll(
        'a.product-link[href*="/products/"]'
      )
      .forEach(function (link) {
        if (
          !link.dataset
            .fitSizeOriginalHref
        ) {
          link.dataset.fitSizeOriginalHref =
            link.getAttribute('href');
        }

        var originalHref =
          link.dataset.fitSizeOriginalHref;

        if (!variantId) {
          link.setAttribute(
            'href',
            originalHref
          );

          return;
        }

        try {
          var url = new URL(
            originalHref,
            window.location.origin
          );

          url.searchParams.delete(
            'variant'
          );

          url.searchParams.set(
            'variant',
            variantId
          );

          link.setAttribute(
            'href',
            url.pathname + url.search
          );
        } catch (error) {
          link.setAttribute(
            'href',
            originalHref
          );
        }
      });
  }

  function evaluateCandidate(
    candidate,
    context,
    size,
    hasKnownSize,
    originalPosition
  ) {
    var relationships =
      getRelationshipData(
        candidate,
        context
      );

    candidate.dataset.originalPosition =
      String(originalPosition);

    storeOriginalProductLinks(candidate);

    /*
     * Without known customer sizing, rank using product
     * relationships and sale/full-price context only.
     */
    if (!hasKnownSize) {
      candidate.dataset.sizeMatch =
        'unknown';

      candidate.dataset
        .matchingVariantId = '';

      candidate.dataset
        .recommendationTier =
          String(
            getRecommendationTier(
              relationships,
              true
            )
          );

      candidate.dataset
        .recommendationScore =
          String(
            getRecommendationScore(
              candidate,
              relationships,
              context
            )
          );

      return Promise.resolve(candidate);
    }

    return getProductData(
      candidate.dataset.productHandle
    )
      .then(function (product) {
        var matchingVariant =
          findSizeVariant(product, size);

        var hasExactSize =
          Boolean(matchingVariant);

        candidate.dataset.sizeMatch =
          hasExactSize
            ? 'true'
            : 'false';

        candidate.dataset
          .matchingVariantId =
            hasExactSize
              ? String(
                  matchingVariant.id
                )
              : '';

        candidate.dataset
          .recommendationTier =
            String(
              getRecommendationTier(
                relationships,
                hasExactSize
              )
            );

        candidate.dataset
          .recommendationScore =
            String(
              getRecommendationScore(
                candidate,
                relationships,
                context
              )
            );

        return candidate;
      })
      .catch(function (error) {
        console.warn(
          'Could not check recommendation size:',
          candidate.dataset.productHandle,
          error
        );

        candidate.dataset.sizeMatch =
          'false';

        candidate.dataset
          .matchingVariantId = '';

        candidate.dataset
          .recommendationTier = '8';

        candidate.dataset
          .recommendationScore =
            String(
              getRecommendationScore(
                candidate,
                relationships,
                context
              )
            );

        return candidate;
      });
  }

  function refreshRecommendations() {
    var section =
      document.querySelector(
        '[data-fit-size-recommendations]'
      );

    var context =
      document.querySelector(
        '[data-fit-size-product-context]'
      );

    if (!section || !context) {
      return;
    }

    var list = section.querySelector(
      '[data-fit-size-product-list]'
    );

    if (!list) {
      return;
    }

    var currentProductId = String(
      context.dataset.productId || ''
    );

    var size = {
      neck: '',
      sleeve: ''
    };

    if (
      window.GantonFitSize &&
      typeof window.GantonFitSize.get ===
        'function'
    ) {
      size = window.GantonFitSize.get();
    }

    if (isIgnoredValue(size.neck)) {
      size.neck = '';
    }

    if (isIgnoredValue(size.sleeve)) {
      size.sleeve = '';
    }

    var hasKnownSize =
      Boolean(size.neck) ||
      Boolean(size.sleeve);

    var thisRefresh =
      ++refreshSequence;

    var allCandidates =
      Array.prototype.slice.call(
        list.querySelectorAll(
          '[data-fit-size-candidate]'
        )
      );

    var candidates =
      allCandidates.filter(
        function (candidate) {
          return (
            String(
              candidate.dataset
                .productId || ''
            ) !== currentProductId
          );
        }
      );

    if (!candidates.length) {
      section.classList.remove(
        'fit-size-recommendations--loading'
      );

      section.removeAttribute(
        'aria-busy'
      );

      return;
    }

    section.classList.add(
      'fit-size-recommendations--loading'
    );

    section.setAttribute(
      'aria-busy',
      'true'
    );

    Promise.all(
      candidates.map(
        function (
          candidate,
          originalPosition
        ) {
          return evaluateCandidate(
            candidate,
            context,
            size,
            hasKnownSize,
            originalPosition
          );
        }
      )
    )
      .then(function () {
        /*
         * Ignore stale responses after another size change.
         */
        if (
          thisRefresh !== refreshSequence
        ) {
          return;
        }

        candidates.sort(
          function (
            candidateA,
            candidateB
          ) {
            var tierDifference =
              Number(
                candidateA.dataset
                  .recommendationTier || 99
              ) -
              Number(
                candidateB.dataset
                  .recommendationTier || 99
              );

            if (tierDifference !== 0) {
              return tierDifference;
            }

            var scoreDifference =
              Number(
                candidateB.dataset
                  .recommendationScore || 0
              ) -
              Number(
                candidateA.dataset
                  .recommendationScore || 0
              );

            if (scoreDifference !== 0) {
              return scoreDifference;
            }

            return (
              Number(
                candidateA.dataset
                  .originalPosition || 0
              ) -
              Number(
                candidateB.dataset
                  .originalPosition || 0
              )
            );
          }
        );

        var selected =
          selectWithDiversity(
            candidates
          );

        allCandidates.forEach(
          function (candidate) {
            candidate.hidden = true;
          }
        );

        selected.forEach(
          function (candidate) {
            list.appendChild(candidate);

            candidate.hidden = false;

            applyProductLinks(candidate);
          }
        );

        candidates.forEach(
          function (candidate) {
            if (
              selected.indexOf(candidate) ===
              -1
            ) {
              list.appendChild(candidate);

              candidate.hidden = true;
            }
          }
        );

        section.classList.remove(
          'fit-size-recommendations--loading'
        );

        section.removeAttribute(
          'aria-busy'
        );

        if (
          window.theme &&
          window.theme
            .ProductBlockManager &&
          typeof window.theme
            .ProductBlockManager
            .loadImages === 'function'
        ) {
          window.theme
            .ProductBlockManager
            .loadImages(section);
        }

        if (
          window.theme &&
          typeof window.theme
            .initAnimateOnScroll ===
            'function'
        ) {
          window.theme
            .initAnimateOnScroll();
        }

        /*
         * Tell the existing swatch engine that recommendation
         * card variant links have been refreshed.
         */
        document.dispatchEvent(
          new CustomEvent(
            'ganton:fit-size-recommendations-updated',
            {
              detail: {
                neck: size.neck,
                sleeve: size.sleeve
              }
            }
          )
        );
      })
      .catch(function (error) {
        console.error(
          'Could not refresh fit and size recommendations.',
          error
        );

        section.classList.remove(
          'fit-size-recommendations--loading'
        );

        section.removeAttribute(
          'aria-busy'
        );
      });
  }

  function scheduleRefresh() {
    window.clearTimeout(
      refreshTimer
    );

    refreshTimer =
      window.setTimeout(
        refreshRecommendations,
        200
      );
  }

  window.GantonFitSizeRecommendations = {
    refresh: refreshRecommendations
  };

  document.addEventListener(
    'ganton:fit-size-change',
    scheduleRefresh
  );

  document.addEventListener(
    'shopify:section:load',
    scheduleRefresh
  );

  if (
    document.readyState === 'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      refreshRecommendations,
      {
        once: true
      }
    );
  } else {
    refreshRecommendations();
  }
})();